<!-- components/UnlockFourDiceModal.vue -->
<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
    <!-- Overlay -->
    <div class="absolute inset-0" @click="close"></div>
    <!-- Modal Container -->
    <div class="relative w-full max-w-lg sm:max-w-2xl h-[80vh] overflow-hidden bg-gray-800 rounded-xl shadow-2xl text-white flex flex-col">
      <div class="flex justify-between items-center p-6 border-b border-gray-700">
        <h2 class="text-xl font-semibold text-cyan-300">จัดการไอเท็มสำหรับปลดล็อก 4 ลูกเต๋า</h2>
        <button @click="close" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <!-- Modal Content -->
      <div class="flex-1 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-cyan-500 scrollbar-track-gray-700">
        <div class="space-y-4 h-60 overflow-y-auto">
          <div v-for="(req, index) in requirements" :key="index" class="p-4 bg-gray-700 rounded-lg animate-fade-in">
            <div class="grid grid-cols-12 items-center gap-3">
              <div class="col-span-10">
                <input
                  v-model="requirements[index]"
                  type="text"
                  placeholder="ชื่อไอเทม (เช่น แผ่นเหล็กกล้า)"
                  class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-cyan-500 focus:outline-none text-white"
                />
              </div>
              <div class="col-span-2">
                <button
                  @click="removeRequirement(index)"
                  class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-300"
                >
                  ลบ
                </button>
              </div>
            </div>
          </div>
          <div class="flex gap-3 mb-3">
            <button
              @click="addRequirement"
              class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-300"
            >
              + เพิ่มไอเท็ม
            </button>
          </div>
        </div>
        <div class="mt-4">
          <label class="block text-sm font-medium text-cyan-300 mb-2">จำนวนไอเท็มที่ต้องใช้</label>
          <input
            v-model="amount"
            type="number"
            min="1"
            placeholder="จำนวนไอเท็มที่ต้องใช้ (เช่น 2)"
            class="w-full px-4 py-2 bg-gray-600 rounded-lg border border-gray-500 focus:ring-2 focus:ring-cyan-500 focus:outline-none text-white"
          />
        </div>
      </div>
      <!-- Fixed Action Buttons -->
      <div class="flex justify-between gap-3 p-6 border-t border-gray-700 bg-gray-800">
        <button
          @click="close"
          class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-300"
        >
          ยกเลิก
        </button>
        <button
          @click="saveRequirements"
          class="px-4 py-2 bg-cyan-600 rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white transition duration-300"
          :disabled="isSavingRequirements"
        >
          {{ isSavingRequirements ? 'กำลังบันทึก...' : 'บันทึก' }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { projectFirestore } from '@/firebase/config';

export default {
  emits: ['close'],
  setup(props, { emit }) {
    const requirements = ref(['']);
    const amount = ref(1);
    const isSavingRequirements = ref(false);

    const unlockFourDiceRef = projectFirestore.collection('server').doc('unlockFourDice');

    const unsubscribe = unlockFourDiceRef.onSnapshot(
      (doc) => {
        if (doc.exists) {
          requirements.value = doc.data().requirements && doc.data().requirements.length > 0 ? doc.data().requirements : [''];
          amount.value = doc.data().amount || 1;
        } else {
          requirements.value = [''];
          amount.value = 1;
        }
      },
      (error) => {
        console.error('Error fetching unlock four dice requirements:', error);
      }
    );

    onUnmounted(() => {
      unsubscribe();
    });

    const close = () => {
      emit('close');
    };

    const addRequirement = () => {
      requirements.value.push('');
    };

    const removeRequirement = (index) => {
      requirements.value.splice(index, 1);
    };

    const saveRequirements = async () => {
      isSavingRequirements.value = true;
      try {
        const filteredRequirements = requirements.value.filter(req => req.trim());
        await unlockFourDiceRef.set({ requirements: filteredRequirements, amount: parseInt(amount.value, 10) }, { merge: true });
        close();
      } catch (error) {
        console.error('Error saving unlock four dice requirements:', error);
      } finally {
        isSavingRequirements.value = false;
      }
    };

    return {
      requirements,
      amount,
      isSavingRequirements,
      addRequirement,
      removeRequirement,
      saveRequirements,
      close
    };
  }
};
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-in;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.scroll-smooth {
  scroll-behavior: smooth;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(147, 51, 234, 0.5) rgba(31, 41, 55, 0.5);
}
.scrollbar-thin::-webkit-scrollbar {
  width: 8px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: rgba(31, 41, 55, 0.5);
  border-radius: 4px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(147, 51, 234, 0.5);
  border-radius: 4px;
  border: 2px solid rgba(31, 41, 55, 0.5);
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgba(147, 51, 234, 0.8);
}
</style>